import {
  Button,
  CircularProgress,
  MessageModal,
  MessageModalProps,
} from "@blacknut/react-sdk";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as AmzLogo } from "../../../assets/dist/ic_amazon.svg";
import nativeBridge from "src/utils/NativeBridge";
import { useDispatch } from "react-redux";
import { logD, logE, logI } from "@blacknut/logging/dist";
import { LOGGING_TAG } from "src/utils/Utils";
import {
  getSubscription,
  NotificationMessage,
  NotificationMessageKind,
  notificationService,
} from "core/index";
import { RemoteNotificationTrigger } from "@blacknut/javascript-sdk/dist";
import { amazonUpdateInfo } from "@blacknut/javascript-sdk/dist/api/User/Action/amazonUpdateInfo";
import { firstValueFrom } from "rxjs";
import { DEBUG_AMAZON } from "src/utils/NativeBridge/AndroidNativeModule";

const AmazonSubscribeModal = (
  props: Partial<MessageModalProps> & {
    isOpen: boolean;
    onClose: () => void;
    onPlanSubscribed: () => void;
    onError: (s: string) => void;
  },
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { onClose, onPlanSubscribed, onError } = props;
  const resumeVideo = useCallback(() => {
    videoToResume.current.forEach((v) => v.play());
  }, []);
  const _onClose = useCallback(() => {
    resumeVideo();
    onClose();
  }, [onClose, resumeVideo]);
  const [inProgress, setInProgress] = useState(false);
  const videoToResume = useRef<HTMLVideoElement[]>([]);
  useEffect(() => {
    const sub = notificationService
      .onMessage()
      .subscribe(async (n: NotificationMessage) => {
        if (n.kind !== NotificationMessageKind.NOTIFICATION_RESP) return;
        const { notification } = n.payload;

        if (notification.trigger === RemoteNotificationTrigger.PLAN_SUBSCRIBED) {
          try {
            await getSubscription()(dispatch);

            onPlanSubscribed();
            setInProgress(false);
            resumeVideo();
          } catch (e) {
            logE(LOGGING_TAG, "Caught error", e);
          }
        }
      });
    return () => {
      sub.unsubscribe();
    };
  }, [dispatch, onPlanSubscribed, resumeVideo]);

  const onAmazonPay = useCallback(async () => {
    setInProgress(true);

    // Fix for video making box to be low on memory
    const $videos = document.querySelectorAll("video");
    $videos.forEach((v) => {
      if (!v.paused) {
        v.pause();
        videoToResume.current.push(v);
      }
    });

    //Reconcilation on customer id
    let amzBillingInfo;
    try {
      if (DEBUG_AMAZON) {
        logI(LOGGING_TAG, "IN getAmazonBillingInfo");
      }
      amzBillingInfo = await nativeBridge.module?.getAmazonBillingInfo?.();
      if (DEBUG_AMAZON) {
        logI(LOGGING_TAG, "OUT getAmazonBillingInfo");
      }
    } catch (e) {
      logE(LOGGING_TAG, "Caught error on getAmazonBillingInfo: %o", e);
      setInProgress(false);
      _onClose();
      onError(e.message);
      return;
    }
    if (amzBillingInfo) {
      logI(LOGGING_TAG, "Got amazon info: %o", amzBillingInfo);
      try {
        if (DEBUG_AMAZON) {
          logI(LOGGING_TAG, "IN amazonUpdateInfo");
        }
        await firstValueFrom(amazonUpdateInfo(amzBillingInfo));
        logD(LOGGING_TAG, "Amazon user info successfully posted");
        if (DEBUG_AMAZON) {
          logI(LOGGING_TAG, "OUT amazonUpdateInfo");
        }
      } catch (error) {
        logE(LOGGING_TAG, "Caught error posting amazon user info %o", error);
      }
    }

    try {
      if (DEBUG_AMAZON) {
        logI(LOGGING_TAG, "IN handleInAppSubscribe");
      }
      await nativeBridge.module?.handleInAppSubscribe?.(dispatch, true);
      if (DEBUG_AMAZON) {
        logI(LOGGING_TAG, "OUT handleInAppSubscribe");
      }
    } catch (e) {
      logE(LOGGING_TAG, "Caught error on amazon pay: %o", e);
      setInProgress(false);
      _onClose();
      onError(e.message);
    }
  }, [_onClose, onError, dispatch]);
  return (
    <MessageModal
      {...props}
      title={t("account.subscriptionStatusTitleTV")}
      message={t("account.subscriptionStatusDesc")}
      buttons={[
        <Button variant="primary" key="OK" onClick={onAmazonPay} disabled={inProgress}>
          <AmzLogo />
          <span>Amazon Pay</span>
          {inProgress && <CircularProgress color="inherit" />}
        </Button>,
        <Button
          variant="secondary"
          key="Cancel"
          onClick={_onClose}
          disabled={inProgress}
        >
          {t("buttons.cancel")}
        </Button>,
      ]}
    />
  );
};

export { AmazonSubscribeModal };
export default AmazonSubscribeModal;
